import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Database, ChartBar, Warning, Wrench, TrendUp, ArrowLeft, ArrowsClockwise, Rocket, Users, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function LegacyReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promotePreview, setPromotePreview] = useState(null);
  const [promoteBusy, setPromoteBusy] = useState(false);
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const loadPromote = async () => {
    try {
      const r = await api.get("/admin/legacy/promote-preview?statuses=assigned,in_progress");
      setPromotePreview(r.data);
    } catch (_) {}
  };

  const loadBackfillPreview = async () => {
    try {
      const r = await api.post("/admin/legacy/backfill-relations?dry_run=true");
      setBackfillPreview(r.data);
    } catch (_) {}
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/legacy-report");
        setData(r.data);
        await loadPromote();
        await loadBackfillPreview();
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load report");
      } finally { setLoading(false); }
    })();
  }, []);

  const runBackfill = async () => {
    if (!backfillPreview) return;
    const toCreate = backfillPreview.to_create?.length || 0;
    const toMerge  = backfillPreview.to_merge?.length || 0;
    if (!toCreate && !toMerge) {
      toast.info("Nothing to backfill — every vendor already has a Sub-Contractor record.");
      return;
    }
    if (!window.confirm(`Create ${toCreate} new Sub-Contractor records and normalize ${toMerge} aliased vendor names on legacy WOs. This is idempotent — safe to re-run. Continue?`)) return;
    setBackfillBusy(true);
    try {
      const r = await api.post("/admin/legacy/backfill-relations?dry_run=false");
      setBackfillResult(r.data);
      toast.success(`Created ${r.data.created_subs ?? toCreate} subs, merged ${r.data.merged_jobs_total ?? 0} WO vendor aliases.`);
      await loadBackfillPreview();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Backfill failed");
    }
    setBackfillBusy(false);
  };

  const promote = async () => {
    if (!promotePreview?.total) return;
    if (!window.confirm(`Promote ${promotePreview.total} legacy WOs (status: assigned/in_progress) to the active Jobs board? They'll show up in your live queue.`)) return;
    setPromoteBusy(true);
    try {
      const r = await api.post("/admin/legacy/promote-to-active", {
        statuses: ["assigned", "in_progress"], dry_run: false,
      });
      toast.success(`Promoted ${r.data.promoted} WOs to active`);
      await loadPromote();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Promote failed"); }
    setPromoteBusy(false);
  };

  return (
    <AdminShell>
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <Link to="/admin/settings" className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest text-slate-500 hover:text-[#0f172a]" data-testid="back-to-settings">
          <ArrowLeft weight="bold" size={12}/> Settings
        </Link>
        <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl uppercase tracking-tight inline-flex items-center gap-2">
          <Database weight="fill" size={32} className="text-amber-500"/> Legacy History Report
        </h1>
        <p className="mt-1 text-sm text-slate-600 font-medium">
          Aggregates every work order imported from your AppFolio CSV (archived rows). Powers Property History &amp; Tenant History behind the scenes.
        </p>

        {loading ? (
          <div className="mt-8 text-center text-slate-500 font-black uppercase text-sm">Loading…</div>
        ) : !data || data.total === 0 ? (
          <div className="mt-8 rugged-card p-8 text-center">
            <div className="text-slate-500 font-black uppercase text-sm">No legacy jobs imported yet.</div>
            <Link to="/admin/settings" className="mt-3 inline-block text-xs font-black uppercase tracking-widest text-[#00C4CC] hover:underline">
              Go to Settings → Import Legacy CSV
            </Link>
          </div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard label="Total Archived" value={data.total} to="/admin/jobs?legacy=1" testid="stat-total"/>
              <StatCard label="Completed" value={data.status_breakdown?.completed || 0} accent="text-emerald-600" to="/admin/jobs?legacy=1&status=completed" testid="stat-completed"/>
              <StatCard label="Canceled" value={data.status_breakdown?.canceled || 0} accent="text-red-600" to="/admin/jobs?legacy=1&status=canceled" testid="stat-canceled"/>
              <StatCard label="Still Open" value={(data.status_breakdown?.assigned || 0) + (data.status_breakdown?.new || 0)} accent="text-amber-600" to="/admin/jobs?legacy=1&status=assigned" testid="stat-open"/>
            </div>
            <p className="mt-2 text-[11px] text-slate-500 font-medium">Click any tile to drill into the underlying work orders.</p>

            {promotePreview && promotePreview.total > 0 && (
              <section className="mt-6 rugged-card p-5 bg-fuchsia-50 border-fuchsia-300">
                <div className="flex items-start gap-3 flex-wrap">
                  <Rocket weight="fill" size={26} className="text-fuchsia-700 shrink-0 mt-1"/>
                  <div className="flex-1 min-w-[240px]">
                    <h2 className="font-display font-black text-xl uppercase text-fuchsia-900">Promote to Active</h2>
                    <p className="mt-1 text-sm text-slate-700">
                      <b>{promotePreview.total}</b> imported WO{promotePreview.total === 1 ? "" : "s"} are still in progress (status: {Object.keys(promotePreview.by_status || {}).join(", ")}). Move them onto the live Jobs board so your crew can act on them today.
                    </p>
                    {promotePreview.by_vendor?.length > 0 && (
                      <div className="mt-2 text-[11px] text-slate-600">
                        <b>By vendor:</b> {promotePreview.by_vendor.slice(0, 5).map(([v, n]) => `${v} (${n})`).join(" · ")}
                      </div>
                    )}
                  </div>
                  <button
                    type="button" onClick={promote} disabled={promoteBusy}
                    data-testid="promote-to-active-btn"
                    className="h-11 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-xs disabled:opacity-50 inline-flex items-center gap-1 shadow-[4px_4px_0px_#0f172a]"
                  >
                    {promoteBusy ? <ArrowsClockwise className="animate-spin" size={14}/> : <Rocket weight="fill" size={14}/>}
                    Promote {promotePreview.total} to Active
                  </button>
                </div>
                {promotePreview.sample?.length > 0 && (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-[10px] font-black uppercase tracking-widest text-fuchsia-800">Preview first {promotePreview.sample.length} rows</summary>
                    <div className="mt-2 max-h-56 overflow-y-auto text-xs bg-white/70 border border-fuchsia-200 rounded p-2">
                      {promotePreview.sample.map(s => (
                        <div key={s.job_id} className="flex items-center gap-2 py-0.5 border-b border-fuchsia-100 last:border-0">
                          <span className="font-mono text-[10px] text-slate-500 w-20 truncate">{s.wo || "—"}</span>
                          <span className="truncate flex-1">{s.address}{s.unit ? ` · ${s.unit}` : ""}</span>
                          <span className="text-[10px] text-slate-500 truncate max-w-[120px]">{s.vendor}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </section>
            )}

            {backfillPreview && (
              <section className="mt-6 rugged-card p-5 bg-cyan-50 border-cyan-300" data-testid="backfill-relations-section">
                <div className="flex items-start gap-3 flex-wrap">
                  <Users weight="fill" size={26} className="text-cyan-700 shrink-0 mt-1"/>
                  <div className="flex-1 min-w-[240px]">
                    <h2 className="font-display font-black text-xl uppercase text-cyan-900">Backfill Sub-Contractors</h2>
                    <p className="mt-1 text-sm text-slate-700">
                      Legacy WOs reference vendors as plain text (e.g. <i>"Best Roofing Inc"</i>). This creates real
                      Sub-Contractor records so you can assign them via Radar Chat, track SLAs, and normalize duplicate
                      spellings.
                    </p>
                    <div className="mt-2 text-[11px] text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
                      <span><b className="text-cyan-800">{backfillPreview.to_create?.length || 0}</b> new subs to create</span>
                      <span><b className="text-fuchsia-700">{backfillPreview.to_merge?.length || 0}</b> aliases to normalize</span>
                      <span><b className="text-slate-700">{backfillPreview.already_exist?.length || 0}</b> already exist</span>
                      <span><b className="text-slate-500">{backfillPreview.total_vendor_strings || 0}</b> distinct vendor strings</span>
                    </div>
                  </div>
                  <button
                    type="button" onClick={runBackfill} disabled={backfillBusy || ((backfillPreview.to_create?.length || 0) + (backfillPreview.to_merge?.length || 0) === 0)}
                    data-testid="backfill-relations-btn"
                    className="h-11 px-4 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase tracking-widest text-xs disabled:opacity-50 inline-flex items-center gap-1 shadow-[4px_4px_0px_#0f172a]"
                  >
                    {backfillBusy ? <ArrowsClockwise className="animate-spin" size={14}/> : <Users weight="fill" size={14}/>}
                    {(backfillPreview.to_create?.length || 0) + (backfillPreview.to_merge?.length || 0) === 0 ? "Nothing to backfill" : "Run backfill"}
                  </button>
                </div>
                {(backfillPreview.to_create?.length > 0 || backfillPreview.to_merge?.length > 0) && (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-[10px] font-black uppercase tracking-widest text-cyan-800">
                      Preview what will happen ({(backfillPreview.to_create?.length || 0) + (backfillPreview.to_merge?.length || 0)} rows)
                    </summary>
                    <div className="mt-2 max-h-64 overflow-y-auto text-xs bg-white/70 border border-cyan-200 rounded p-2 space-y-1">
                      {backfillPreview.to_create?.map(s => (
                        <div key={"c-" + s.name} className="flex items-center gap-2 py-0.5 border-b border-cyan-100 last:border-0">
                          <span className="inline-block h-5 px-1.5 bg-cyan-600 text-white text-[9px] font-black uppercase tracking-widest">create</span>
                          <span className="truncate flex-1 font-black">{s.name}</span>
                          <span className="text-[10px] text-slate-500">{s.job_count} WO{s.job_count === 1 ? "" : "s"}</span>
                        </div>
                      ))}
                      {backfillPreview.to_merge?.map(m => (
                        <div key={"m-" + m.vendor} className="flex items-center gap-2 py-0.5 border-b border-cyan-100 last:border-0">
                          <span className="inline-block h-5 px-1.5 bg-fuchsia-600 text-white text-[9px] font-black uppercase tracking-widest">merge</span>
                          <span className="truncate flex-1"><span className="text-slate-500">{m.vendor}</span> → <b>{m.canonical}</b></span>
                          <span className="text-[10px] text-slate-500">{m.job_count} WO{m.job_count === 1 ? "" : "s"}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                {backfillResult && (
                  <div className="mt-3 p-2 bg-emerald-100 border-2 border-emerald-500 text-xs text-emerald-900">
                    <b>Done:</b> {backfillResult.created_subs ?? 0} new Sub-Contractor{(backfillResult.created_subs ?? 0) === 1 ? "" : "s"} created,{" "}
                    {backfillResult.merged_jobs_total ?? 0} legacy WO vendor names normalized.{" "}
                    <Link to="/admin/sub-contractors" className="underline font-black" data-testid="backfill-view-subs-link">View Sub-Contractors →</Link>
                  </div>
                )}
              </section>
            )}

            <section className="mt-8 rugged-card p-5">
              <h2 className="font-display font-black text-xl uppercase inline-flex items-center gap-2">
                <TrendUp weight="fill" size={20}/> Top Issue Tags
              </h2>
              <p className="mt-1 text-xs text-slate-500 font-medium">Which problems come up most across your portfolio</p>
              <div className="mt-4 space-y-2" data-testid="top-issues">
                {(data.top_issues || []).slice(0, 15).map((row, i) => {
                  const pct = data.total ? (100 * row.count / data.total) : 0;
                  return (
                    <div key={row.issue} className="flex items-center gap-3">
                      <div className="w-6 text-xs font-mono text-slate-400">#{i + 1}</div>
                      <div className="w-40 sm:w-56 text-sm font-black truncate">{row.issue}</div>
                      <div className="flex-1 h-5 bg-slate-100 relative">
                        <div className="h-full bg-[#00C4CC]" style={{ width: `${Math.min(pct * 3, 100)}%` }}/>
                      </div>
                      <div className="w-12 text-xs font-mono font-black text-right">{row.count}</div>
                    </div>
                  );
                })}
                {(data.top_issues || []).length === 0 && <div className="text-xs text-slate-500">No issue tags recorded</div>}
              </div>
            </section>

            <section className="mt-6 rugged-card p-5 bg-red-50 border-red-300">
              <h2 className="font-display font-black text-xl uppercase inline-flex items-center gap-2 text-red-800">
                <Warning weight="fill" size={20}/> Repeat-Offender Tenants
              </h2>
              <p className="mt-1 text-xs text-slate-600 font-medium">Residents with 3+ work orders — good candidates for lease review</p>
              <div className="mt-4 overflow-x-auto" data-testid="repeat-offenders">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] font-black uppercase tracking-widest text-slate-500 border-b-2 border-slate-300">
                      <th className="py-2 pr-4">Resident</th>
                      <th className="py-2 pr-4">Total WO</th>
                      <th className="py-2 pr-4">Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.repeat_offenders || []).map(o => (
                      <tr key={o.resident} className="border-b border-slate-200">
                        <td className="py-2 pr-4 font-black">{o.resident}</td>
                        <td className="py-2 pr-4 font-mono">
                          <span className={`px-2 py-0.5 rounded-sm ${o.total >= 10 ? "bg-red-600 text-white" : o.total >= 5 ? "bg-orange-500 text-white" : "bg-amber-200 text-slate-800"}`}>
                            {o.total}
                          </span>
                        </td>
                        <td className="py-2 pr-4 font-mono text-emerald-700">{o.completed}</td>
                      </tr>
                    ))}
                    {(data.repeat_offenders || []).length === 0 && (
                      <tr><td colSpan={3} className="py-3 text-xs text-slate-500">No repeat offenders (nobody has 3+ WOs yet)</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mt-6 rugged-card p-5">
              <h2 className="font-display font-black text-xl uppercase inline-flex items-center gap-2">
                <Wrench weight="fill" size={20}/> Vendor Scorecard
              </h2>
              <p className="mt-1 text-xs text-slate-500 font-medium">Completion rate by vendor across archived jobs</p>
              <div className="mt-4 overflow-x-auto" data-testid="vendor-scorecard">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] font-black uppercase tracking-widest text-slate-500 border-b-2 border-slate-300">
                      <th className="py-2 pr-4">Vendor</th>
                      <th className="py-2 pr-4">Total</th>
                      <th className="py-2 pr-4">Completed</th>
                      <th className="py-2 pr-4">Canceled</th>
                      <th className="py-2 pr-4">Completion %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.vendor_scorecard || []).map(v => (
                      <tr key={v.vendor} className="border-b border-slate-200">
                        <td className="py-2 pr-4 font-black truncate max-w-xs" title={v.vendor}>
                          <div className="truncate">{v.vendor}</div>
                          {v.specialty && (
                            <span className={`inline-block mt-0.5 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-sm ${
                              v.specialty === "HVAC" ? "bg-cyan-600 text-white" :
                              v.specialty === "Plumbing" ? "bg-blue-600 text-white" :
                              v.specialty === "Drain Cleaning" ? "bg-blue-500 text-white" :
                              v.specialty === "Service Line Insurance" ? "bg-indigo-600 text-white" :
                              v.specialty === "Water Heater" ? "bg-sky-600 text-white" :
                              v.specialty === "Electrical" ? "bg-amber-500 text-white" :
                              v.specialty === "Roofing" ? "bg-orange-600 text-white" :
                              v.specialty === "Garage Door" ? "bg-yellow-600 text-white" :
                              v.specialty === "Fire Safety" ? "bg-red-700 text-white" :
                              v.specialty === "Emergency Response" ? "bg-red-600 text-white" :
                              v.specialty === "Pest Control" ? "bg-lime-700 text-white" :
                              v.specialty === "Landscaping" ? "bg-green-700 text-white" :
                              v.specialty === "Appliance" ? "bg-fuchsia-600 text-white" :
                              v.specialty === "Cleaning" ? "bg-teal-600 text-white" :
                              v.specialty === "Concrete / Masonry" ? "bg-stone-600 text-white" :
                              v.specialty === "Property Mgmt" ? "bg-purple-600 text-white" :
                              v.specialty === "General Contractor" ? "bg-slate-700 text-white" :
                              v.specialty === "In-House Crew" ? "bg-emerald-600 text-white" :
                              "bg-violet-500 text-white"
                            }`}>
                              {v.specialty}
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-4 font-mono">{v.total}</td>
                        <td className="py-2 pr-4 font-mono text-emerald-700">{v.completed}</td>
                        <td className="py-2 pr-4 font-mono text-red-600">{v.canceled}</td>
                        <td className="py-2 pr-4 font-mono font-black">
                          <span className={v.completion_rate >= 80 ? "text-emerald-700" : v.completion_rate >= 50 ? "text-amber-600" : "text-red-600"}>
                            {v.completion_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                    {(data.vendor_scorecard || []).length === 0 && (
                      <tr><td colSpan={5} className="py-3 text-xs text-slate-500">No vendor data yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </AdminShell>
  );
}

function StatCard({ label, value, accent = "text-[#0f172a]", testid, to }) {
  const inner = (
    <>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 font-display font-black text-3xl ${accent}`}>{value.toLocaleString()}</div>
    </>
  );
  return to ? (
    <Link to={to} data-testid={testid} className="rugged-card p-4 block hover:-translate-y-0.5 transition-transform cursor-pointer">
      {inner}
    </Link>
  ) : (
    <div className="rugged-card p-4" data-testid={testid}>{inner}</div>
  );
}
